import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Row,
  type FormInstance,
} from "@ir/ui";
import { getInputValue } from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import {
  generatePortalKeyFromName,
  isPortalKeyFormSubmittable,
  PORTAL_KEY_FORMAT_MESSAGE,
  PORTAL_KEY_MAX_LENGTH,
  PORTAL_KEY_MAX_MESSAGE,
  PORTAL_KEY_NAME_MAX_LENGTH,
  PORTAL_KEY_NAME_MAX_MESSAGE,
  PORTAL_KEY_NAME_REQUIRED_MESSAGE,
  PORTAL_KEY_PATTERN,
  PORTAL_KEY_REQUIRED_MESSAGE,
  sanitizePortalKeyInput,
  type PurchaserPortalKeyFormValues,
} from "./purchaserPortalKeyFormUtils";

interface PurchaserPortalKeyFormProps {
  form: FormInstance<PurchaserPortalKeyFormValues>;
  mode: "create" | "edit";
  initialValues: PurchaserPortalKeyFormValues;
  isSaving: boolean;
  isFormSubmittable: boolean;
  onCancel: () => void;
  onSubmit: (values: PurchaserPortalKeyFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const PurchaserPortalKeyForm = ({
  form,
  mode,
  initialValues,
  isSaving,
  isFormSubmittable,
  onCancel,
  onSubmit,
  onFormValidityChange,
}: PurchaserPortalKeyFormProps) => {
  const [manualKeySource, setManualKeySource] =
    useState<PurchaserPortalKeyFormValues | null>(null);
  const isKeyEditedManually = manualKeySource === initialValues;

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);
    const values = form.getFieldsValue();
    const isSubmittable =
      !hasErrors &&
      isPortalKeyFormSubmittable(mode, values, initialValues.portalKeyName);
    onFormValidityChange?.(isSubmittable);
  }, [form, initialValues.portalKeyName, mode, onFormValidityChange]);

  useEffect(() => {  
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const nameRules = useMemo(
    () => [
      {
        required: true,
        whitespace: true,
        message: PORTAL_KEY_NAME_REQUIRED_MESSAGE,
      },
      {
        max: PORTAL_KEY_NAME_MAX_LENGTH,
        message: PORTAL_KEY_NAME_MAX_MESSAGE,
      },
    ],
    [],
  );

  const keyRules = useMemo(
    () =>
      mode === "create"
        ? [
            {
              required: true,
              whitespace: true,
              message: PORTAL_KEY_REQUIRED_MESSAGE,
            },
            {
              max: PORTAL_KEY_MAX_LENGTH,
              message: PORTAL_KEY_MAX_MESSAGE,
            },
            {
              pattern: PORTAL_KEY_PATTERN,
              message: PORTAL_KEY_FORMAT_MESSAGE,
            },
          ]
        : [],
    [mode],
  );

  const handleNameChange = (valueOrEvent: unknown) => {
    const name = getInputValue(valueOrEvent);
    const nextValues: Partial<PurchaserPortalKeyFormValues> = {
      portalKeyName: name,
    };
    if (mode === "create" && !isKeyEditedManually) {
      nextValues.portalKey = generatePortalKeyFromName(name);
      form.setFields([{ name: "portalKey", errors: [] }]);
    }
    form.setFieldsValue(nextValues);
    const fields =
      mode === "create" && !isKeyEditedManually
        ? (["portalKeyName", "portalKey"] as const)
        : (["portalKeyName"] as const);
    form.validateFields([...fields]).catch(() => {});
  };

  const handleKeyChange = (valueOrEvent: unknown) => {
    setManualKeySource(initialValues);
    form.setFields([{ name: "portalKey", errors: [] }]);
    form.setFieldsValue({
      portalKey: sanitizePortalKeyInput(getInputValue(valueOrEvent)),
    });
    form.validateFields(["portalKey"]).catch(() => {});
  };

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={onSubmit}
      onFieldsChange={updateFormValidity}
      onValuesChange={updateFormValidity}
      className="purchaser-portal-key-form"
    >
      <Row>
        <Col span={5}>
          <Form.Item
            label="Portal Key Name"
            name="portalKeyName"
            validateTrigger={["onChange", "onBlur"]}
            rules={nameRules}
          >
            <Input
              placeholder="Enter portal key name"
              onChange={handleNameChange}
              onInput={handleNameChange}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item
            label="Portal Key"
            name="portalKey"
            validateTrigger={["onChange", "onBlur"]}
            rules={keyRules}
          >
            <Input
              placeholder="DOT_FOODS"
              isDisabled={mode === "edit"}
              readOnly={mode === "edit"}
              onChange={mode === "create" ? handleKeyChange : undefined}
              onInput={mode === "create" ? handleKeyChange : undefined}
            />
          </Form.Item>
        </Col>
        <Col span={2}>
          <Flex align="center" gap={0.5} className="mt-4 pt-2">
            <Button
              size="small"
              color="primary"
              variant="clear"
              onPress={() => form.submit()}
              isLoading={isSaving}
              isDisabled={isSaving || !isFormSubmittable}
              aria-label="Save portal key"
              className="mt-1"
            >
              <Icon name={Icons.Check} />
            </Button>
            <Button
              size="small"
              color="grey"
              variant="clear"
              onPress={onCancel}
              isDisabled={isSaving}
              aria-label="Cancel"
              className="mt-1"
            >
              <Icon name={Icons.Close} />
            </Button>
          </Flex>
        </Col>
      </Row>
    </Form>
  );
};
